import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { IRule } from '@/classes/rules/IRule';
import { DefaultValue } from './DefaultValue';
import { Invalidation } from '@/classes/rules/Invalidation';
import { FieldUsage } from '@/classes/rules/FieldUsage';

const alwaysAvailable = () => true;
const noDefaultValue = () => null;

/**
 * IRule to handle enums, check that the value is contained in the enum values.
 */
export class EnumRule<Context = ScenarioExt> implements IRule<Context> {
  public readonly key: string;
  public readonly enumRef: Record<string, string>;
  public required: boolean;
  public displayed: boolean = true;
  public isAvailable: (context: Context) => boolean;
  public overrideGetDefaultValue: (context?: Context) => DefaultValue<string> | null;
  public filterOptions: ([key, value]: [string, string], context?: Context) => boolean;
  public fieldUsage: FieldUsage;

  public constructor(key: string, enumRef: Record<string, string>) {
    this.key = key;
    this.enumRef = enumRef;
    this.required = true;
    this.isAvailable = alwaysAvailable;
    this.overrideGetDefaultValue = noDefaultValue;
    this.filterOptions = alwaysAvailable;
    this.fieldUsage = new FieldUsage();
  }

  public get invalidation(): Invalidation {
    return this.fieldUsage.getInvalidation();
  }

  public getDefaultValue(context?: Context): DefaultValue<string> | null {
    const options = this.getValidEntries(context);
    if (options.length === 1) {
      const [[, uniqueValue]] = options;
      return new DefaultValue(uniqueValue);
    }
    return this.overrideGetDefaultValue(context);
  }

  public checkValue(value: string | null, context: Context): boolean | null {
    if (value === null) {
      return null;
    }
    return this.getValidEntries(context)
      .map(([, value]) => value)
      .includes(value);
  }

  public getOptions(context?: Context): string[] {
    return this.getValidEntries(context)
      .map(([key]) => key);
  }

  private getValidEntries(context?: Context): [string, string][] {
    return Object.entries(this.enumRef)
      .filter(entry => this.filterOptions(entry, context));
  }

  public withIsAvailable(isAvailable: (context: Context) => boolean): this {
    this.isAvailable = isAvailable;
    return this;
  }

  public withDefaultValue(getDefaultValue: (context?: Context) => DefaultValue<string> | null): this {
    this.overrideGetDefaultValue = getDefaultValue;
    return this;
  }

  public withRequired(isRequired?: boolean): this { // true is default setting for this function
    this.required = isRequired !== false;
    return this;
  }

  public withFilterOptions(filterOptions: ([key, value]: [string, string], context?: Context) => boolean): this {
    this.filterOptions = filterOptions;
    return this;
  }

  public withFieldUsage(fieldUsage: FieldUsage): this {
    this.fieldUsage = fieldUsage;
    return this;
  }

  public vValidateRule(context?: Context): string {
    const constraints = [];
    if (this.required) {
      constraints.push('required');
    }
    const validValues = this.getValidEntries(context)
      .map(([, value]) => value)
      .join(',');
    constraints.push(`included:${validValues}`);
    return constraints.join('|');
  }
}
